import { computed, inject } from '@angular/core';
import { ApiService } from '@libs/core/base-config';
import {
  AsyncState,
  asResult,
  computedFailure,
  dataFromState,
  initialState,
  loadingState,
  successState,
} from '@libs/core/lib/state/async-states';
import {
  buyNow,
  calculateShippingCost,
  cancelOrder,
  getOrderById,
  getPublicOrder,
  getSubOrderChat,
  markChatMessageAsSeen,
  markOrderAsProcessing,
  markOrderAsReadyForShipping,
  ordersList,
  ordersListCustomers,
  placeOrder,
  sendChatMessage,
  updateChatMessage,
} from '@libs/mtjr/shared/generated/entities/order/service/order.service';
import { Dtos } from '@libs/mtjr/shared/generated/types';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';

export function withOrdersList() {
  type OrdersListState = {
    ordersListState: AsyncState<Dtos.OrdersListResponse>;
    ordersListInput: Dtos.OrdersListQueryParams;
    ordersListCanFetchNext: boolean;
    ordersListFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<OrdersListState>({
      ordersListState: initialState(),
      ordersListInput: { page: 1, totalPerPage: 20 },
      ordersListCanFetchNext: false,
      ordersListFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async ordersListPage(input?: Partial<Dtos.OrdersListQueryParams>) {
        const mergedInput = { ...store.ordersListInput(), ...input };
        patchState(store, {
          ordersListState: mergedInput.page === 1 ? loadingState() : store.ordersListState(),
          ordersListInput: mergedInput,
          ordersListFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(ordersList(apiService, store.ordersListInput()));
        if (result.success) {
          const cState = store.ordersListState();
          let orders = result.data.orders;
          if (cState.success && mergedInput.page > 1) {
            orders = [...cState.data.orders, ...orders];
          }
          patchState(store, {
            ordersListState: successState({ ...result.data, orders: orders }),
            ordersListCanFetchNext: result.data.orders.length === mergedInput.totalPerPage,
            ordersListFetchingNext: false,
          });
        } else {
          patchState(store, {
            ordersListState: result,
            ordersListCanFetchNext: false,
            ordersListFetchingNext: false,
          });
        }
      },

      async ordersListFetchNextPage() {
        if (store.ordersListCanFetchNext() === false) return;
        const input = store.ordersListInput();
        await this.ordersListPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      orders: dataFromState(store.ordersListState, (data) => data?.orders ?? []),
      ordersFailure: computedFailure(store.ordersListState),
    })),
  );
}

export function withOrdersListCustomers() {
  type OrdersListCustomersState = {
    ordersListCustomersState: AsyncState<Dtos.OrdersListCustomersResponse>;
    ordersListCustomersInput: Dtos.OrdersListOrdersListCustomersQueryParams;
    ordersListCustomersCanFetchNext: boolean;
    ordersListCustomersFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<OrdersListCustomersState>({
      ordersListCustomersState: initialState(),
      ordersListCustomersInput: { page: 1, totalPerPage: 20 },
      ordersListCustomersCanFetchNext: false,
      ordersListCustomersFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async ordersListCustomersPage(input?: Partial<Dtos.OrdersListOrdersListCustomersQueryParams>) {
        const mergedInput = { ...store.ordersListCustomersInput(), ...input };
        patchState(store, {
          ordersListCustomersState: mergedInput.page === 1 ? loadingState() : store.ordersListCustomersState(),
          ordersListCustomersInput: mergedInput,
          ordersListCustomersFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(ordersListCustomers(apiService, store.ordersListCustomersInput()));
        if (result.success) {
          const cState = store.ordersListCustomersState();
          let orders = result.data.orders;
          if (cState.success && mergedInput.page > 1) {
            orders = [...cState.data.orders, ...orders];
          }
          patchState(store, {
            ordersListCustomersState: successState({ ...result.data, orders: orders }),
            ordersListCustomersCanFetchNext: result.data.orders.length === mergedInput.totalPerPage,
            ordersListCustomersFetchingNext: false,
          });
        } else {
          patchState(store, {
            ordersListCustomersState: result,
            ordersListCustomersCanFetchNext: false,
            ordersListCustomersFetchingNext: false,
          });
        }
      },

      async ordersListCustomersFetchNextPage() {
        if (store.ordersListCustomersCanFetchNext() === false) return;
        const input = store.ordersListCustomersInput();
        await this.ordersListCustomersPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      orders: dataFromState(store.ordersListCustomersState, (data) => data?.orders ?? []),
      ordersFailure: computedFailure(store.ordersListCustomersState),
    })),
  );
}

export function withCalculateShippingCost() {
  return signalStoreFeature(
    withState<{ calculateShippingCostState: AsyncState<Dtos.OrderProductForShippingResponse> }>({
      calculateShippingCostState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async calculateShippingCost(calculateShippingCostBody: Dtos.CalculateShippingCostBody) {
        patchState(store, { calculateShippingCostState: loadingState() });
        const result = await asResult(calculateShippingCost(apiService, calculateShippingCostBody));
        patchState(store, { calculateShippingCostState: result });
      },
    })),
    withComputed((store) => ({
      calculateShippingCostLoading: computed(() => store.calculateShippingCostState().type === 'loading'),
      calculateShippingCostFailure: computedFailure(store.calculateShippingCostState),
    })),
  );
}

export function withMarkOrderAsReadyForShipping() {
  return signalStoreFeature(
    withState<{ markOrderAsReadyForShippingState: AsyncState<void> }>({
      markOrderAsReadyForShippingState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async markOrderAsReadyForShipping({ orderId, subOrderId }: Dtos.MarkOrderAsReadyForShippingPathParams) {
        patchState(store, { markOrderAsReadyForShippingState: loadingState() });
        const result = await asResult(markOrderAsReadyForShipping(apiService, { orderId, subOrderId }));
        patchState(store, { markOrderAsReadyForShippingState: result });
      },
    })),
    withComputed((store) => ({
      markOrderAsReadyForShippingLoading: computed(() => store.markOrderAsReadyForShippingState().type === 'loading'),
      markOrderAsReadyForShippingFailure: computedFailure(store.markOrderAsReadyForShippingState),
    })),
  );
}

export function withMarkOrderAsProcessing() {
  return signalStoreFeature(
    withState<{ markOrderAsProcessingState: AsyncState<void> }>({
      markOrderAsProcessingState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async markOrderAsProcessing({ orderId, subOrderId }: Dtos.MarkOrderAsProcessingPathParams) {
        patchState(store, { markOrderAsProcessingState: loadingState() });
        const result = await asResult(markOrderAsProcessing(apiService, { orderId, subOrderId }));
        patchState(store, { markOrderAsProcessingState: result });
      },
    })),
    withComputed((store) => ({
      markOrderAsProcessingLoading: computed(() => store.markOrderAsProcessingState().type === 'loading'),
      markOrderAsProcessingFailure: computedFailure(store.markOrderAsProcessingState),
    })),
  );
}

export function withCancelOrder() {
  return signalStoreFeature(
    withState<{ cancelOrderState: AsyncState<void> }>({
      cancelOrderState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async cancelOrder(cancelOrderBody: Dtos.CancelOrderBody, { orderId, subOrderId }: Dtos.CancelOrderPathParams) {
        patchState(store, { cancelOrderState: loadingState() });
        const result = await asResult(cancelOrder(apiService, cancelOrderBody, { orderId, subOrderId }));
        patchState(store, { cancelOrderState: result });
      },
    })),
    withComputed((store) => ({
      cancelOrderLoading: computed(() => store.cancelOrderState().type === 'loading'),
      cancelOrderFailure: computedFailure(store.cancelOrderState),
    })),
  );
}

export function withSendChatMessage() {
  return signalStoreFeature(
    withState<{ sendChatMessageState: AsyncState<void> }>({
      sendChatMessageState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async sendChatMessage(
        sendChatMessageBody: Dtos.SendChatMessageBody,
        { orderId, subOrderId }: Dtos.SendChatMessagePathParams,
      ) {
        patchState(store, { sendChatMessageState: loadingState() });
        const result = await asResult(sendChatMessage(apiService, sendChatMessageBody, { orderId, subOrderId }));
        patchState(store, { sendChatMessageState: result });
      },
    })),
    withComputed((store) => ({
      sendChatMessageLoading: computed(() => store.sendChatMessageState().type === 'loading'),
      sendChatMessageFailure: computedFailure(store.sendChatMessageState),
    })),
  );
}

export function withMarkChatMessageAsSeen() {
  return signalStoreFeature(
    withState<{ markChatMessageAsSeenState: AsyncState<void> }>({
      markChatMessageAsSeenState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async markChatMessageAsSeen({ orderId, subOrderId, messageId }: Dtos.MarkChatMessageAsSeenPathParams) {
        patchState(store, { markChatMessageAsSeenState: loadingState() });
        const result = await asResult(markChatMessageAsSeen(apiService, { orderId, subOrderId, messageId }));
        patchState(store, { markChatMessageAsSeenState: result });
      },
    })),
    withComputed((store) => ({
      markChatMessageAsSeenLoading: computed(() => store.markChatMessageAsSeenState().type === 'loading'),
      markChatMessageAsSeenFailure: computedFailure(store.markChatMessageAsSeenState),
    })),
  );
}

export function withUpdateChatMessage() {
  return signalStoreFeature(
    withState<{ updateChatMessageState: AsyncState<void> }>({
      updateChatMessageState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async updateChatMessage(
        updateChatMessageBody: Dtos.UpdateChatMessageBody,
        { orderId, subOrderId, messageId }: Dtos.UpdateChatMessagePathParams,
      ) {
        patchState(store, { updateChatMessageState: loadingState() });
        const result = await asResult(
          updateChatMessage(apiService, updateChatMessageBody, { orderId, subOrderId, messageId }),
        );
        patchState(store, { updateChatMessageState: result });
      },
    })),
    withComputed((store) => ({
      updateChatMessageLoading: computed(() => store.updateChatMessageState().type === 'loading'),
      updateChatMessageFailure: computedFailure(store.updateChatMessageState),
    })),
  );
}

export function withPlaceOrder() {
  return signalStoreFeature(
    withState<{ placeOrderState: AsyncState<Dtos.Order> }>({
      placeOrderState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async placeOrder(placeOrderBody: Dtos.PlaceOrderBody) {
        patchState(store, { placeOrderState: loadingState() });
        const result = await asResult(placeOrder(apiService, placeOrderBody));
        patchState(store, { placeOrderState: result });
      },
    })),
    withComputed((store) => ({
      placeOrderLoading: computed(() => store.placeOrderState().type === 'loading'),
      placeOrderFailure: computedFailure(store.placeOrderState),
    })),
  );
}

export function withBuyNow() {
  return signalStoreFeature(
    withState<{ buyNowState: AsyncState<Dtos.Order> }>({
      buyNowState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async buyNow(buyNowBody: Dtos.BuyNowBody) {
        patchState(store, { buyNowState: loadingState() });
        const result = await asResult(buyNow(apiService, buyNowBody));
        patchState(store, { buyNowState: result });
      },
    })),
    withComputed((store) => ({
      buyNowLoading: computed(() => store.buyNowState().type === 'loading'),
      buyNowFailure: computedFailure(store.buyNowState),
    })),
  );
}

export function withGetSubOrderChat() {
  return signalStoreFeature(
    withState<{ getSubOrderChatState: AsyncState<Dtos.GetSubOrderChatResponse> }>({
      getSubOrderChatState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async getSubOrderChat({ orderId, subOrderId }: Dtos.GetSubOrderChatPathParams) {
        patchState(store, { getSubOrderChatState: loadingState() });
        const result = await asResult(getSubOrderChat(apiService, { orderId, subOrderId }));
        patchState(store, { getSubOrderChatState: result });
      },
    })),
    withComputed((store) => ({
      getSubOrderChatLoading: computed(() => store.getSubOrderChatState().type === 'loading'),
      getSubOrderChatFailure: computedFailure(store.getSubOrderChatState),
    })),
  );
}

export function withGetOrderById() {
  return signalStoreFeature(
    withState<{ getOrderByIdState: AsyncState<Dtos.Order> }>({
      getOrderByIdState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async getOrderById({ id }: Dtos.GetOrderByIdPathParams) {
        patchState(store, { getOrderByIdState: loadingState() });
        const result = await asResult(getOrderById(apiService, { id }));
        patchState(store, { getOrderByIdState: result });
      },
    })),
    withComputed((store) => ({
      getOrderByIdLoading: computed(() => store.getOrderByIdState().type === 'loading'),
      getOrderByIdFailure: computedFailure(store.getOrderByIdState),
    })),
  );
}

export function withGetPublicOrder() {
  return signalStoreFeature(
    withState<{ getPublicOrderState: AsyncState<Dtos.Order> }>({
      getPublicOrderState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async getPublicOrder({ orderId }: Dtos.GetPublicOrderPathParams) {
        patchState(store, { getPublicOrderState: loadingState() });
        const result = await asResult(getPublicOrder(apiService, { orderId }));
        patchState(store, { getPublicOrderState: result });
      },
    })),
    withComputed((store) => ({
      getPublicOrderLoading: computed(() => store.getPublicOrderState().type === 'loading'),
      getPublicOrderFailure: computedFailure(store.getPublicOrderState),
    })),
  );
}
